import { WithOwner } from "../IDBSchema";
import { EntityDataApi } from "../IndexedDBApi";
import { CarryInBase, CarryInBasePatchPayload, CarryInBasePutPayload } from "../../../schema/generated/CarryIn";
import { carryInRow, carryInRows } from "../../../schema/console/carry_in";
import { getFromIdPayload, getFromIntIdPayload, setDeletePayload } from "../../../schema/general";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { qrScanProcessPayload } from "../../../schema/console/qrScan";
import { CarryInForMaterialSearch } from "../../../schema/console/material";
import { WorkBase } from "../../../schema/generated/Work";
import { idbEntityDefs } from "../generated/entityDefs";
import Time from "../../../util/Time";
import { required } from "../../../util/typed";

export class CarryInDataApi extends EntityDataApi<CarryInBase> {
  public readonly def = idbEntityDefs.CarryIn;

  public async getAll(ownerId: string): Promise<CarryInBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<CarryInBase | null> {
    return await this.findById(payload);
  }

  // 工事(Work, id=number)を id 索引した Map を作る。工事は生成された薄い DataApi(Works ストア)に
  // キャッシュされる。includeDeleted で履歴解決(材料検索)と alive 限定(搬入一覧/詳細)を切り替える。
  private async workMapByOwner(ownerId: string, includeDeleted: boolean): Promise<Map<number, WorkBase>> {
    const works = await this.IDB().generated.work.getAllByOwner(ownerId, !includeDeleted);
    return new Map(works.map((work) => [work.id, work]));
  }

  public async getCarryInForMaterialSearch(payload: WithOwner<getFromIdPayload>): Promise<CarryInForMaterialSearch | null> {
    const carryIn = await this.getById(payload);
    if (!carryIn) {
      return null;
    }
    const user = await this.IDB().userDataApi.getFullDataById({ id: carryIn.created_by_user_id, ownerId: payload.ownerId });
    // 材料検索は履歴解決(削除済みの工事も含めて解決する)。
    const works = await this.workMapByOwner(payload.ownerId, true);
    const work = carryIn.work_id != null ? works.get(carryIn.work_id) ?? null : null;
    return { ...carryIn, User: user, Work: work };
  }

  public async getRowById(payload: WithOwner<getFromIdPayload>): Promise<carryInRow | null> {
    const carryIn = await this.getById(payload);
    if (!carryIn) {
      return null;
    }
    const materials = await this.IDB().materialDataApi.getByCarryInId(carryIn.id, payload.ownerId);
    const user = await this.IDB().userDataApi.getFullDataById({ id: carryIn.created_by_user_id, ownerId: payload.ownerId });
    // 搬入一覧/詳細は alive の工事のみ解決する(オンラインの include where deleted_at:null と同方針)。
    const works = await this.workMapByOwner(payload.ownerId, false);
    const work = carryIn.work_id != null ? works.get(carryIn.work_id) ?? null : null;
    return { ...carryIn, Materials: materials, User: user, Work: work };
  }

  public async getRowsByWorkId(payload: WithOwner<getFromIntIdPayload>): Promise<carryInRows> {
    const carryIns = (await this.getAll(payload.ownerId)).filter((carryIn) => carryIn.work_id === payload.id);
    const materials = await this.IDB().materialDataApi.getAll(payload.ownerId);
    const works = await this.workMapByOwner(payload.ownerId, false);
    const userIds = [...new Set(carryIns.map((carryIn) => carryIn.created_by_user_id))];
    const users = await Promise.all(
      userIds.map((id) =>
        this.IDB().userDataApi.getFullDataById({ id, ownerId: payload.ownerId })
      )
    );
    const userMap = new Map(users.map((user) => [user.id, user]));

    return carryIns.map((carryIn) => ({
      ...carryIn,
      Materials: materials.filter((material) => material.carry_in_id === carryIn.id),
      User: required(userMap.get(carryIn.created_by_user_id), "登録者ユーザー"),
      Work: carryIn.work_id != null ? works.get(carryIn.work_id) ?? null : null,
    }));
  }

  public async put(data: WithOwner<CarryInBasePutPayload>): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async patch(data: WithOwner<CarryInBasePatchPayload>): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    return await this.softDelete(data);
  }

  // QRスキャンによる搬入処理。親行(搬入時刻)の patch と材料行の紐付け更新を1トランザクションで
  // まとめて書き込む(途中失敗で親だけ/材料だけ更新される中途半端な状態を防ぐ)。
  public async process(payload: WithOwner<qrScanProcessPayload>): Promise<StatusResponse> {
    const carryIn = await this.getById(payload);
    if (!carryIn) {
      throw new Error("搬入データがありません。");
    }

    const materials = await this.IDB().materialDataApi.getAll(payload.ownerId);
    const selected = new Set(payload.MaterialIDs);
    const toDetach = materials.filter((m) => m.carry_in_id === payload.id && !selected.has(m.id)).map((m) => ({ id: m.id, carry_in_id: null }));
    const toAttach = payload.MaterialIDs.map((id) => ({ id, carry_in_id: payload.id }));
    const materialRows = await this.IDB().materialDataApi.buildLinkRows([...toAttach, ...toDetach], payload.ownerId);

    const carryInRow = { ...carryIn, carried_in_at: Time.Now().GetTime(), ...this.addPatchBaseColumns() };

    await this.IDB().putRowsAtomic([
      { store: this.def.store, rows: [carryInRow] },
      { store: this.IDB().materialDataApi.def.store, rows: materialRows },
    ]);

    return { status: "ok" };
  }
}
